import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import HttpStatus from 'http-status-codes';

import {
    getErrorDisplayMessageFromResponse,
    getErrorMessageFromResponse,
    getResponseStatusCode,
} from '../../../../../../utils/errorUtil';
import { fetchImageBlobFromUrl } from '../../../../imageFetchUtils';
import { getBackgroundRemovedFilename } from '../../toolbar/imageModalBackgroundRemovalFormatUtils';
import { removeBackground, uploadProcessedImage } from '../../toolbar/imageModalBackgroundRemovalService';
import { updateElement } from '../../../../../actions/elementActions';
import type { ImMNElement, MNImageContent } from '../../../../../../../common/elements/elementModelTypes';
import { INDETERMINATE_BACKGROUND_REMOVAL_PROGRESS, type BackgroundRemovalProgress } from '../../toolbar/imageModalBackgroundRemovalProgressTypes';
import { buildBackgroundRemovalCacheChanges, hasBackgroundRemovalCache } from '../../toolbar/imageModalBackgroundRemovalUtils';
import { trackImageBackgroundRemovalClicked } from '../../toolbar/imageModalBackgroundRemovalAnalyticsUtils';
import { ELEMENT_ATTACHMENT_CANCEL_UPLOAD } from '../../../../../../../common/elements/elementConstants';

const BACKGROUND_REMOVAL_ERROR_MESSAGE = 'You have exhausted your monthly usage limit';
const GENERIC_BACKGROUND_REMOVAL_ERROR_MESSAGE = 'Background removal failed';

type UseImageModalV2BackgroundRemovalParams = {
    element: ImMNElement;
    elementId: string;
    cropSourceImage: MNImageContent;
    largestImageUrl: string | undefined;
    imageType: string;
    setProgress: (progress: BackgroundRemovalProgress | null) => void;
    // Whether the removed-background variant is staged to be applied when the user saves. The processed
    // image is cached on the element immediately, but the variant is only applied on save.
    stagedActive: boolean;
    onChangeStagedActive: (active: boolean) => void;
};

type UseImageModalV2BackgroundRemovalResult = {
    onToggle: () => void;
    errorMessage: string | null;
    clearError: () => void;
};

/**
 * Orchestrate server-backed background removal. The processed image is cached immediately to avoid
 * repeat provider calls, while applying the removed-background variant is staged until the user saves.
 */
export const useImageModalV2BackgroundRemoval = ({
    element,
    elementId,
    cropSourceImage,
    largestImageUrl,
    imageType,
    setProgress,
    stagedActive,
    onChangeStagedActive,
}: UseImageModalV2BackgroundRemovalParams): UseImageModalV2BackgroundRemovalResult => {
    const dispatch = useDispatch();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Dismissing background mode unmounts the toolbar, which cancels the in-flight removal: the provider
    // request is aborted and the element's registered upload is cancelled. The ref only holds a controller
    // while a removal is running, so the cleanup only fires when that upload can be the removal's own.
    const abortControllerRef = useRef<AbortController | null>(null);
    useEffect(
        () => () => {
            if (!abortControllerRef.current) return;

            abortControllerRef.current.abort();
            dispatch({ type: ELEMENT_ATTACHMENT_CANCEL_UPLOAD, id: elementId });
        },
        [dispatch, elementId],
    );

    const handleFailure = (error: unknown) => {
        // The request uses an arraybuffer response, so the JSON error body isn't readable here — detect the
        // usage limit via the 429 status the server tags it with instead.
        if (getResponseStatusCode(error) === HttpStatus.TOO_MANY_REQUESTS) {
            return setErrorMessage(BACKGROUND_REMOVAL_ERROR_MESSAGE);
        }

        const message =
            getErrorDisplayMessageFromResponse(error) ||
            getErrorMessageFromResponse(error) ||
            (error instanceof Error ? error.message : GENERIC_BACKGROUND_REMOVAL_ERROR_MESSAGE);

        setErrorMessage(message);
    };

    const removeBackgroundFromImage = async (imageUrl: string) => {
        // The ref can only track (and abort) one run, so never start a second removal concurrently.
        if (abortControllerRef.current) return;

        setErrorMessage(null);
        setProgress(INDETERMINATE_BACKGROUND_REMOVAL_PROGRESS);

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        try {
            const imageBlob = await fetchImageBlobFromUrl(imageUrl, imageType);
            if (abortController.signal.aborted) return;

            const fileName = getBackgroundRemovedFilename(imageUrl, imageType, imageBlob);
            const processedBlob = await removeBackground(imageBlob, fileName, setProgress, abortController.signal);
            if (abortController.signal.aborted) return;

            const withoutBackgroundSource = await uploadProcessedImage({
                dispatch,
                elementId,
                fileName,
                blob: processedBlob,
            });
            if (abortController.signal.aborted) return;

            // Persist only the processed source cache so the provider is never called again. The visible
            // image is left untouched; applying the removed-background variant is deferred until save.
            dispatch(
                updateElement({
                    id: elementId,
                    changes: buildBackgroundRemovalCacheChanges({
                        withBackgroundSource: cropSourceImage,
                        withoutBackgroundSource,
                    }),
                }),
            );

            onChangeStagedActive(true);
        } catch (error) {
            // A cancelled removal is not a failure — the user dismissed background mode mid-request.
            if (abortController.signal.aborted) return;

            handleFailure(error);
        } finally {
            if (abortControllerRef.current === abortController) abortControllerRef.current = null;
            // An aborted run means the toolbar has unmounted — skip the state update.
            if (!abortController.signal.aborted) setProgress(null);
        }
    };

    const onToggle = () => {
        if (!largestImageUrl) return;

        if (stagedActive) return onChangeStagedActive(false);

        if (hasBackgroundRemovalCache(element)) return onChangeStagedActive(true);

        trackImageBackgroundRemovalClicked();
        return removeBackgroundFromImage(largestImageUrl);
    };

    return {
        onToggle,
        errorMessage,
        clearError: () => setErrorMessage(null),
    };
};
